import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Percent, X, AlertCircle, Calculator, Save } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import Pagination, { usePagination } from './Pagination';
import DeleteImpactModal from './DeleteImpactModal';

const CommissionRatioManagement = () => {
  const [ratios, setRatios] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRecalcModal, setShowRecalcModal] = useState(false);
  const [editingRatio, setEditingRatio] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    groupLeaderRatio: '',
    teamLeaderRatio: '',
    agentRatio: '',
    isActive: true,
  });
  const [recalcData, setRecalcData] = useState({
    ratio: null,
    affectedProducts: [],
    newCommissions: [],
  });
  const [editingProductInModal, setEditingProductInModal] = useState(null);
  const [productEdits, setProductEdits] = useState({}); // Store all product edits: { productId: { groupLeaderComm, teamLeaderComm, agentComm } }

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const [ratiosRes, productsRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/commission-ratios`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/products`, { headers: getAuthHeaders() }),
      ]);

      const ratiosResult = await ratiosRes.json();
      const productsResult = await productsRes.json();

      if (ratiosResult.success) {
        setRatios(ratiosResult.data);
      }

      if (productsResult.success) {
        setProducts(productsResult.data);
      }
    } catch (err) {
      console.error('Fetch data error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate total ratio (in percentages)
  const calculateTotal = () => {
    const gl = parseFloat(formData.groupLeaderRatio) || 0;
    const tl = parseFloat(formData.teamLeaderRatio) || 0;
    const agent = parseFloat(formData.agentRatio) || 0;
    return (gl + tl + agent).toFixed(2);
  };

  const handleCreateRatio = async (e) => {
    e.preventDefault();
    try {
      setError('');

      const total = parseFloat(calculateTotal());
      if (Math.abs(total - 100.0) > 0.01) {
        setError('Ratios must sum to 100%');
        return;
      }

      // Convert percentages to decimals for backend
      const response = await fetch(`${getApiUrl()}/api/commission-ratios`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          groupLeaderRatio: parseFloat(formData.groupLeaderRatio) / 100,
          teamLeaderRatio: parseFloat(formData.teamLeaderRatio) / 100,
          agentRatio: parseFloat(formData.agentRatio) / 100,
          isActive: formData.isActive,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setFormData({
          name: '',
          groupLeaderRatio: '',
          teamLeaderRatio: '',
          agentRatio: '',
          isActive: true,
        });
        setShowCreateModal(false);
        fetchData();
      } else {
        // Show detailed error message if available
        const errorMsg = result.errors 
          ? result.errors.map(e => e.msg || e.message).join(', ')
          : result.message || 'Failed to create commission ratio';
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Create ratio error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleEditClick = (ratio) => {
    setEditingRatio(ratio);
    // Convert decimals to percentages for display
    setFormData({
      name: ratio.name,
      groupLeaderRatio: (parseFloat(ratio.groupLeaderRatio) * 100).toString(),
      teamLeaderRatio: (parseFloat(ratio.teamLeaderRatio) * 100).toString(),
      agentRatio: (parseFloat(ratio.agentRatio) * 100).toString(),
      isActive: ratio.isActive,
    });
    setShowEditModal(true);
  };

  const handleUpdateRatio = async (e) => {
    e.preventDefault();
    try {
      setError('');

      const total = parseFloat(calculateTotal());
      if (Math.abs(total - 100.0) > 0.01) {
        setError('Ratios must sum to 100%');
        return;
      }

      // Check if ratio values changed (compare percentages)
      const currentGLPercent = parseFloat(editingRatio.groupLeaderRatio) * 100;
      const currentTLPercent = parseFloat(editingRatio.teamLeaderRatio) * 100;
      const currentAgentPercent = parseFloat(editingRatio.agentRatio) * 100;
      
      const glChanged = Math.abs(parseFloat(formData.groupLeaderRatio) - currentGLPercent) > 0.01;
      const tlChanged = Math.abs(parseFloat(formData.teamLeaderRatio) - currentTLPercent) > 0.01;
      const agentChanged = Math.abs(parseFloat(formData.agentRatio) - currentAgentPercent) > 0.01;
      const ratioChanged = glChanged || tlChanged || agentChanged;

      // Find products using this ratio
      const affectedProducts = products.filter((p) => p.commissionRatioId === editingRatio.id);

      if (ratioChanged && affectedProducts.length > 0) {
        // Calculate new commissions for affected products (convert percentages to decimals)
        const newCommissions = affectedProducts.map((product) => {
          const maxComm = parseFloat(product.maxCommPercentage);
          return {
            product,
            newGL: (maxComm * (parseFloat(formData.groupLeaderRatio) / 100)).toFixed(4),
            newTL: (maxComm * (parseFloat(formData.teamLeaderRatio) / 100)).toFixed(4),
            newAgent: (maxComm * (parseFloat(formData.agentRatio) / 100)).toFixed(4),
          };
        });

        setRecalcData({
          ratio: editingRatio,
          affectedProducts,
          newCommissions,
        });
        setShowRecalcModal(true);
        return; // Don't update yet, wait for user confirmation
      }

      // No products affected or no ratio change, update directly (convert percentages to decimals)
      const response = await fetch(`${getApiUrl()}/api/commission-ratios/${editingRatio.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          groupLeaderRatio: parseFloat(formData.groupLeaderRatio) / 100,
          teamLeaderRatio: parseFloat(formData.teamLeaderRatio) / 100,
          agentRatio: parseFloat(formData.agentRatio) / 100,
          isActive: formData.isActive,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setShowEditModal(false);
        setEditingRatio(null);
        setFormData({
          name: '',
          groupLeaderRatio: '',
          teamLeaderRatio: '',
          agentRatio: '',
          isActive: true,
        });
        fetchData();
      } else {
        // Show detailed error message if available
        const errorMsg = result.errors 
          ? result.errors.map(e => e.msg || e.message).join(', ')
          : result.message || 'Failed to update commission ratio';
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Update ratio error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleEditProductInModal = (product) => {
    // Save current edits if switching products
    if (editingProductInModal && editingProductInModal !== product.id) {
      // Current edits are already in productEdits state, no need to save again
    }
    
    setEditingProductInModal(product.id);
    
    // Load existing edits or use current product values
    if (productEdits[product.id]) {
      // Use existing edits
    } else {
      // Initialize with current product values
      setProductEdits({
        ...productEdits,
        [product.id]: {
          groupLeaderComm: product.groupLeaderComm.toString(),
          teamLeaderComm: product.teamLeaderComm.toString(),
          agentComm: product.agentComm.toString(),
        },
      });
    }
  };

  const handleSaveProductInModal = async (product) => {
    try {
      setError('');
      const edits = productEdits[product.id];
      if (!edits) {
        setError('No changes to save');
        return;
      }

      const response = await fetch(`${getApiUrl()}/api/products/${product.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          groupLeaderComm: parseFloat(edits.groupLeaderComm),
          teamLeaderComm: parseFloat(edits.teamLeaderComm),
          agentComm: parseFloat(edits.agentComm),
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Update the product in recalcData with the new saved values
        const savedGL = parseFloat(edits.groupLeaderComm);
        const savedTL = parseFloat(edits.teamLeaderComm);
        const savedAgent = parseFloat(edits.agentComm);
        
        const updatedCommissions = recalcData.newCommissions.map((item) => {
          if (item.product.id === product.id) {
            return {
              ...item,
              product: {
                ...item.product,
                groupLeaderComm: savedGL,
                teamLeaderComm: savedTL,
                agentComm: savedAgent,
              },
              // Update the "new" values to match saved values
              newGL: savedGL.toFixed(4),
              newTL: savedTL.toFixed(4),
              newAgent: savedAgent.toFixed(4),
            };
          }
          return item;
        });

        setRecalcData({
          ...recalcData,
          newCommissions: updatedCommissions,
        });

        // Remove from edits since it's saved
        const newEdits = { ...productEdits };
        delete newEdits[product.id];
        setProductEdits(newEdits);
        setEditingProductInModal(null);

        // Refresh products data
        const productsRes = await fetch(`${getApiUrl()}/api/products`, { headers: getAuthHeaders() });
        const productsResult = await productsRes.json();
        if (productsResult.success) {
          setProducts(productsResult.data);
        }
      } else {
        setError(result.message || 'Failed to update product');
      }
    } catch (err) {
      console.error('Save product error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleCancelProductEdit = (productId) => {
    // Remove edits for this product
    const newEdits = { ...productEdits };
    delete newEdits[productId];
    setProductEdits(newEdits);
    setEditingProductInModal(null);
  };

  const handleUpdateProductEdit = (productId, field, value) => {
    setProductEdits({
      ...productEdits,
      [productId]: {
        ...(productEdits[productId] || {}),
        [field]: value,
      },
    });
  };

  const handleSaveRatio = async () => {
    try {
      setError('');

      // First, save all pending product edits
      const productIds = Object.keys(productEdits);
      if (productIds.length > 0) {
        const savePromises = productIds.map(async (productId) => {
          const edits = productEdits[productId];
          const response = await fetch(`${getApiUrl()}/api/products/${productId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              groupLeaderComm: parseFloat(edits.groupLeaderComm),
              teamLeaderComm: parseFloat(edits.teamLeaderComm),
              agentComm: parseFloat(edits.agentComm),
            }),
          });
          return response.json();
        });

        const results = await Promise.all(savePromises);
        const failedResults = results.filter(r => !r.success);
        
        if (failedResults.length > 0) {
          setError(failedResults.map(r => r.message || 'Failed to update product').join(', '));
          return;
        }

        // Update recalcData with saved values
        const updatedCommissions = recalcData.newCommissions.map((item) => {
          const edits = productEdits[item.product.id];
          if (edits) {
            const savedGL = parseFloat(edits.groupLeaderComm);
            const savedTL = parseFloat(edits.teamLeaderComm);
            const savedAgent = parseFloat(edits.agentComm);
            return {
              ...item,
              product: {
                ...item.product,
                groupLeaderComm: savedGL,
                teamLeaderComm: savedTL,
                agentComm: savedAgent,
              },
              newGL: savedGL.toFixed(4),
              newTL: savedTL.toFixed(4),
              newAgent: savedAgent.toFixed(4),
            };
          }
          return item;
        });

        setRecalcData({
          ...recalcData,
          newCommissions: updatedCommissions,
        });
      }

      // Then update the ratio (convert percentages to decimals)
      const updateResponse = await fetch(`${getApiUrl()}/api/commission-ratios/${editingRatio.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          groupLeaderRatio: parseFloat(formData.groupLeaderRatio) / 100,
          teamLeaderRatio: parseFloat(formData.teamLeaderRatio) / 100,
          agentRatio: parseFloat(formData.agentRatio) / 100,
          isActive: formData.isActive,
        }),
      });

      const updateResult = await updateResponse.json();

      if (updateResult.success) {
        setShowRecalcModal(false);
        setShowEditModal(false);
        setEditingRatio(null);
        setFormData({
          name: '',
          groupLeaderRatio: '',
          teamLeaderRatio: '',
          agentRatio: '',
          isActive: true,
        });
        setRecalcData({ ratio: null, affectedProducts: [], newCommissions: [] });
        setProductEdits({});
        setEditingProductInModal(null);
        fetchData();
      } else {
        setError(updateResult.message || 'Failed to update commission ratio');
      }
    } catch (err) {
      console.error('Save ratio error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleDeleteRatio = async (id) => {
    try {
      setError('');
      const response = await fetch(`${getApiUrl()}/api/commission-ratios/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        setDeleteConfirm(null);
        fetchData();
      } else {
        setError(result.message || 'Failed to delete commission ratio');
        setDeleteConfirm(null);
      }
    } catch (err) {
      console.error('Delete ratio error:', err);
      setError('Unable to connect to server');
      setDeleteConfirm(null);
    }
  };

  // Pagination
  const {
    currentPage,
    paginatedItems: paginatedRatios,
    totalItems,
    handlePageChange,
  } = usePagination(ratios, 10);

  const renderModal = (isEdit = false) => {
    const modalTitle = isEdit ? 'Edit Commission Ratio' : 'Create New Commission Ratio';
    const submitLabel = isEdit ? 'Update Ratio' : 'Create Ratio';
    const onSubmit = isEdit ? handleUpdateRatio : handleCreateRatio;
    const total = calculateTotal();
    const isValid = Math.abs(parseFloat(total) - 100.0) < 0.01;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="neo-modal p-6 w-full max-w-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">{modalTitle}</h3>
            <button
              onClick={() => {
                if (isEdit) {
                  setShowEditModal(false);
                  setEditingRatio(null);
                } else {
                  setShowCreateModal(false);
                }
                setFormData({
                  name: '',
                  groupLeaderRatio: '',
                  teamLeaderRatio: '',
                  agentRatio: '',
                  isActive: true,
                });
                setError('');
              }}
              className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Ratio Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none"
                  placeholder="e.g., Default Ratio"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Group Leader Ratio (%) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.groupLeaderRatio}
                  onChange={(e) => setFormData({...formData, groupLeaderRatio: e.target.value})}
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="10"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Team Leader Ratio (%) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.teamLeaderRatio}
                  onChange={(e) => setFormData({...formData, teamLeaderRatio: e.target.value})}
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="25"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Agent Ratio (%) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.agentRatio}
                  onChange={(e) => setFormData({...formData, agentRatio: e.target.value})}
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="65"
                  required
                />
              </div>

              {/* Total Validation */}
              <div className={`p-3 rounded-md ${isValid ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium dark:text-dark-text">
                    {isValid ? 'Total:' : 'Total (must equal 100%):'}
                  </span>
                  <span className={`text-sm font-bold ${isValid ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                    {total}% ({isValid ? '✓ Valid' : '✗ Invalid'})
                  </span>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  className="h-4 w-4 text-primary-600 dark:text-primary-400 focus:ring-primary-500 border-gray-300 dark:border-dark-border rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900 dark:text-dark-text">
                  Set as active ratio
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  if (isEdit) {
                    setShowEditModal(false);
                    setEditingRatio(null);
                  } else {
                    setShowCreateModal(false);
                  }
                  setFormData({
                    name: '',
                    groupLeaderRatio: '',
                    teamLeaderRatio: '',
                    agentRatio: '',
                    isActive: true,
                  });
                  setError('');
                }}
                className="px-4 py-2 neo-btn text-gray-700 dark:text-dark-text"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isValid}
                className="px-4 py-2 neo-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 dark:text-primary-400 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-600 dark:text-dark-muted">Loading commission ratios...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Error Message */}
      {error && !showCreateModal && !showEditModal && !showRecalcModal && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Action Button */}
      <div className="flex items-center justify-end mb-6">
        <button
          onClick={() => {
            setShowCreateModal(true);
            setFormData({
              name: '',
              groupLeaderRatio: '',
              teamLeaderRatio: '',
              agentRatio: '',
              isActive: true,
            });
            setError('');
          }}
          className="neo-btn-primary px-4 py-2 text-sm font-medium flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create Commission Ratio</span>
        </button>
      </div>

      {/* Ratios Table */}
      <div className="neo-raised overflow-hidden">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200 dark:border-dark-border">
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text">All Commission Ratios</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
            <thead className="bg-gray-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Ratio Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Group Leader
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Team Leader
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Products
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/70 dark:divide-dark-border">
              {paginatedRatios.map((ratio) => (
                <tr key={ratio.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center mr-3">
                        <Percent className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="text-sm font-medium text-gray-900 dark:text-dark-text">{ratio.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-dark-text">
                      {(parseFloat(ratio.groupLeaderRatio) * 100).toFixed(2)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-dark-text">
                      {(parseFloat(ratio.teamLeaderRatio) * 100).toFixed(2)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-dark-text">
                      {(parseFloat(ratio.agentRatio) * 100).toFixed(2)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-dark-text">
                      {ratio._count?.products || 0} product(s)
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {ratio.isActive ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEditClick(ratio)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(ratio.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={10}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Create Modal */}
      {showCreateModal && renderModal(false)}

      {/* Edit Modal */}
      {showEditModal && renderModal(true)}

      {/* Recalculate Confirmation Modal */}
      {showRecalcModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="neo-modal p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">Update Product Commissions</h3>
              <button
                onClick={() => {
                  setShowRecalcModal(false);
                  setRecalcData({ ratio: null, affectedProducts: [], newCommissions: [] });
                  setEditingProductInModal(null);
                  setProductEdits({});
                }}
                className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="mb-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 px-4 py-3 rounded-lg">
              <p className="text-sm">
                The commission ratio has been updated. Please review and edit the following {recalcData.affectedProducts.length} product(s) if needed:
              </p>
            </div>

            <div className="mb-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
                  <thead className="bg-gray-50 dark:bg-slate-800/50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase">Product</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase">Max Comm</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-dark-muted uppercase">Group Leader</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-dark-muted uppercase">Team Leader</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-dark-muted uppercase">Agent</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-dark-muted uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200/70 dark:divide-dark-border">
                    {recalcData.newCommissions.map((item, index) => {
                      const isEditing = editingProductInModal === item.product.id;
                      const hasUnsavedChanges = productEdits[item.product.id] !== undefined;
                      const currentEdits = productEdits[item.product.id] || {
                        groupLeaderComm: item.product.groupLeaderComm.toString(),
                        teamLeaderComm: item.product.teamLeaderComm.toString(),
                        agentComm: item.product.agentComm.toString(),
                      };
                      
                      // Determine display values - use edits if available, otherwise use calculated new values
                      const displayGL = hasUnsavedChanges 
                        ? parseFloat(currentEdits.groupLeaderComm).toFixed(4)
                        : item.newGL;
                      const displayTL = hasUnsavedChanges
                        ? parseFloat(currentEdits.teamLeaderComm).toFixed(4)
                        : item.newTL;
                      const displayAgent = hasUnsavedChanges
                        ? parseFloat(currentEdits.agentComm).toFixed(4)
                        : item.newAgent;
                      
                      return (
                        <tr key={index} className={hasUnsavedChanges ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-dark-text">{item.product.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-dark-text">{parseFloat(item.product.maxCommPercentage).toFixed(2)}%</td>
                          {isEditing ? (
                            <>
                              <td className="px-4 py-2">
                                <input
                                  type="number"
                                  step="0.0001"
                                  min="0"
                                  value={currentEdits.groupLeaderComm}
                                  onChange={(e) => handleUpdateProductEdit(item.product.id, 'groupLeaderComm', e.target.value)}
                                  onWheel={(e) => e.target.blur()}
                                  className="w-full px-2 py-1 neo-field dark:text-dark-text text-sm text-center focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  type="number"
                                  step="0.0001"
                                  min="0"
                                  value={currentEdits.teamLeaderComm}
                                  onChange={(e) => handleUpdateProductEdit(item.product.id, 'teamLeaderComm', e.target.value)}
                                  onWheel={(e) => e.target.blur()}
                                  className="w-full px-2 py-1 neo-field dark:text-dark-text text-sm text-center focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  type="number"
                                  step="0.0001"
                                  min="0"
                                  value={currentEdits.agentComm}
                                  onChange={(e) => handleUpdateProductEdit(item.product.id, 'agentComm', e.target.value)}
                                  onWheel={(e) => e.target.blur()}
                                  className="w-full px-2 py-1 neo-field dark:text-dark-text text-sm text-center focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => handleSaveProductInModal(item.product)}
                                    className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 transition-colors"
                                    title="Save"
                                  >
                                    <Save className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleCancelProductEdit(item.product.id)}
                                    className="text-gray-600 dark:text-dark-muted hover:text-gray-900 dark:hover:text-dark-text transition-colors"
                                    title="Cancel"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-2 text-sm text-center">
                                <span className="text-gray-500 dark:text-dark-muted line-through mr-2">{parseFloat(item.product.groupLeaderComm).toFixed(4)}%</span>
                                <span className={`font-medium ${hasUnsavedChanges ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                                  {displayGL}%
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-center">
                                <span className="text-gray-500 dark:text-dark-muted line-through mr-2">{parseFloat(item.product.teamLeaderComm).toFixed(4)}%</span>
                                <span className={`font-medium ${hasUnsavedChanges ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                                  {displayTL}%
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-center">
                                <span className="text-gray-500 dark:text-dark-muted line-through mr-2">{parseFloat(item.product.agentComm).toFixed(4)}%</span>
                                <span className={`font-medium ${hasUnsavedChanges ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                                  {displayAgent}%
                                </span>
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex items-center justify-center">
                                  <button
                                    onClick={() => handleEditProductInModal(item.product)}
                                    className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 transition-colors"
                                    title="Edit"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRecalcModal(false);
                  setRecalcData({ ratio: null, affectedProducts: [], newCommissions: [] });
                  setEditingProductInModal(null);
                  setProductEdits({});
                }}
                className="px-4 py-2 neo-btn text-gray-700 dark:text-dark-text"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRatio}
                className="px-4 py-2 neo-btn-primary flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteImpactModal
        open={!!deleteConfirm}
        entityType="commissionRatio"
        entityId={deleteConfirm}
        title="Delete commission ratio"
        onClose={() => setDeleteConfirm(null)}
        onDeleted={() => { setDeleteConfirm(null); fetchData(); }}
      />
    </div>
  );
};

export default CommissionRatioManagement;

